import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { CircleCard } from '../components/CircleCard';
import type { HomeDashboard, HomeUrgentItem, UserContext } from '@kinapp/shared';

function UrgentItemRow({ item, onNavigate }: { item: HomeUrgentItem; onNavigate: (circleId: string) => void }) {
  const typeLabel = item.type === 'task' ? 'Task' : item.type === 'medication' ? 'Medication' : 'Appointment';
  const typeColor =
    item.type === 'task'
      ? 'bg-amber-100 text-amber-800'
      : item.type === 'medication'
      ? 'bg-red-100 text-red-800'
      : 'bg-blue-100 text-blue-800';

  return (
    <button
      onClick={() => onNavigate(item.circleId)}
      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 bg-white hover:border-primary-300 hover:shadow-sm transition-all text-left"
    >
      <span className={`shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded ${typeColor}`}>
        {typeLabel}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900 truncate">{item.title}</p>
        <p className="text-xs text-gray-500 truncate">
          {item.dependentName} · {item.circleName}
          {item.dueAt && ` · ${new Date(item.dueAt).toLocaleDateString()}`}
        </p>
      </div>
      <svg className="shrink-0 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );
}

export function HomePage() {
  const { workspaces, switchWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const [home, setHome] = useState<HomeDashboard | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .getHomeDashboard()
      .then((data) => setHome(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const goToCircle = (familyId: string) => {
    switchWorkspace(familyId);
    navigate('/dashboard');
  };

  const circles: UserContext[] = home?.circles ?? workspaces
    .filter((w) => w.type === 'family')
    .map((w) => ({
      familyId: w.id,
      name: w.name,
      label: w.label,
      role: 'MEMBER',
      planTier: 'personal',
      dependentCount: 0,
      memberCount: 0,
    }));

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Home</h1>
        <p className="text-sm text-gray-500 mt-1">Your circles at a glance</p>
      </div>

      {/* Urgent items */}
      {!loading && home && home.urgentItems.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            Needs attention
          </h2>
          <div className="space-y-2">
            {home.urgentItems.map((item, i) => (
              <UrgentItemRow key={i} item={item} onNavigate={goToCircle} />
            ))}
          </div>
        </section>
      )}

      {/* Circle cards */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            Your Circles
          </h2>
          <button
            onClick={() => navigate('/onboarding')}
            className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Add Circle
          </button>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[0, 1].map((i) => (
              <div key={i} className="h-24 rounded-xl bg-gray-100 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {circles.map((ctx) => {
              const urgentCount = home?.urgentItems.filter((item) => item.circleId === ctx.familyId).length ?? 0;
              return (
                <CircleCard
                  key={ctx.familyId}
                  context={ctx}
                  urgentCount={urgentCount}
                  onClick={() => goToCircle(ctx.familyId)}
                  onEditLabel={async (label) => {
                    await api.updateCircleLabel(ctx.familyId, label);
                    const fresh = await api.getHomeDashboard();
                    setHome(fresh);
                  }}
                />
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
